import React from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Paper,
  TableContainer,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { blue, blueGrey } from '@mui/material/colors';
import { Role, LabourCostRate, LabourBillingRate } from 'api/labourApi';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  RATE_CARD_WORKING_AWAY_COST_HEADER_TOOLTIP,
  RATE_CARD_WORKING_AWAY_BILLING_HEADER_TOOLTIP,
  WORKING_AWAY_INFO_ARIA_LABEL,
  WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
} from 'constants/workingAwayFromHomeCopy';

interface WorkingAwayFromHomeRatesTableComponentProps {
  costRates: LabourCostRate[];
  billingRates: LabourBillingRate[];
  onRoleClick?: (role: Role, rateType: 'cost' | 'billing') => void;
}

const ALL_ROLES = {
  [Role.Administrator]: 'Administrator',
  [Role.ProjectManager]: 'Project Coordinator',
  [Role.Supervisor]: 'Supervisor',
  [Role.Technician]: 'Technician',
};

const getBillingBranchKey = (rate: LabourBillingRate): string => {
  if (rate.branchId && rate.clientId) {
    return `${rate.branch?.name || 'Unknown Branch'}/${
      rate.client?.legalName || 'Unknown Client'
    }`;
  } else if (rate.clientId) {
    return rate.client?.legalName || 'Unknown Client';
  } else if (rate.branchId) {
    return rate.branch?.name || 'Unknown Branch';
  } else if (rate.isDefault) {
    return 'Default';
  }
  return 'Unknown';
};

export const WorkingAwayFromHomeRatesTableComponent: React.FC<
  WorkingAwayFromHomeRatesTableComponentProps
> = ({ costRates, billingRates, onRoleClick }) => {
  const allBillingBranches = Array.from(
    new Set(billingRates?.map(getBillingBranchKey) || [])
  )
    .filter(Boolean)
    .sort((a, b) => {
      if (a === 'Default') return -1;
      if (b === 'Default') return 1;
      return a.localeCompare(b);
    });

  const getLatestCostRateForRole = (role: Role): LabourCostRate | null => {
    const roleCostRates = costRates.filter((r) => r.role === role);
    if (roleCostRates.length === 0) return null;

    return roleCostRates.reduce((latest, current) => {
      return new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
        ? current
        : latest;
    }, roleCostRates[0]);
  };

  const getLatestWafhBillingRateForRoleAndBranch = (
    role: Role,
    branchKey: string
  ): number | null => {
    const matchingRates = billingRates.filter(
      (rate) => rate.role === role && getBillingBranchKey(rate) === branchKey
    );

    if (matchingRates.length === 0) return null;

    const latestRate = matchingRates.reduce((latest, current) => {
      return new Date(current.effectiveFrom) > new Date(latest.effectiveFrom)
        ? current
        : latest;
    }, matchingRates[0]);

    return Number(latestRate.workingAwayFromHomeBillingRate);
  };

  const renderHeaderWithTooltip = (
    label: string,
    tooltip: string,
    background: string
  ) => (
    <TableCell
      sx={{
        background,
        minWidth: 120,
        zIndex: 4,
        position: 'sticky',
        top: 57,
        borderRight: '1px solid #ccc',
        fontWeight: 'bold',
      }}
      align="center">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.25,
        }}>
        <Typography component="span" variant="inherit" fontWeight="bold">
          {label}
        </Typography>
        <Tooltip
          arrow
          title={
            <Typography
              component="div"
              variant="body2"
              sx={{
                ...WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
                maxWidth: 280,
              }}>
              {tooltip}
            </Typography>
          }>
          <IconButton
            size="small"
            aria-label={WORKING_AWAY_INFO_ARIA_LABEL}
            sx={{ p: 0.25 }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    </TableCell>
  );

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                background: blueGrey[50],
                minWidth: 150,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              rowSpan={2}>
              Role
            </TableCell>
            <TableCell
              sx={{
                background: blueGrey[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center"
              colSpan={1}>
              Cost Rates
            </TableCell>
            <TableCell
              sx={{
                background: blue[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
                fontWeight: 'bold',
              }}
              align="center"
              colSpan={allBillingBranches.length || 1}>
              Billing Rates
            </TableCell>
          </TableRow>
          <TableRow>
            {renderHeaderWithTooltip(
              'Rate',
              RATE_CARD_WORKING_AWAY_COST_HEADER_TOOLTIP,
              blueGrey[50]
            )}
            {allBillingBranches.length > 0 ? (
              allBillingBranches.map((b) => (
                <TableCell
                  sx={{
                    background: blue[50],
                    minWidth: 150,
                    zIndex: 4,
                    position: 'sticky',
                    top: 57,
                    borderRight: '1px solid #ccc',
                  }}
                  key={b}
                  align="center">
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{
                          ...WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
                          maxWidth: 280,
                        }}>
                        {RATE_CARD_WORKING_AWAY_BILLING_HEADER_TOOLTIP}
                      </Typography>
                    }>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 0.5,
                      }}>
                      {b.split('/').map((part, index) => (
                        <Chip
                          key={`${b}-${index}`}
                          label={part}
                          sx={{
                            margin: '2px',
                            color: '#000',
                            backgroundColor: blue[200],
                            paddingInline: '0.5rem',
                            paddingY: '1rem',
                          }}
                          size="small"
                          icon={
                            index === 0 ? (
                              <BusinessIcon
                                color="action"
                                sx={{ fontSize: '1rem', color: '#000' }}
                              />
                            ) : (
                              <AccountBoxIcon
                                color="action"
                                sx={{ fontSize: '1rem', color: '#000' }}
                              />
                            )
                          }
                        />
                      ))}
                    </Box>
                  </Tooltip>
                </TableCell>
              ))
            ) : (
              <TableCell
                sx={{
                  background: blue[50],
                  zIndex: 4,
                  position: 'sticky',
                  top: 57,
                  borderRight: '1px solid #ccc',
                }}
                align="center">
                <Typography variant="caption">
                  No Billing Rates Added Yet
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(ALL_ROLES).map((roleKey) => {
            const role = roleKey as Role;
            const costRate = getLatestCostRateForRole(role);

            return (
              <TableRow key={role} hover>
                <TableCell
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    background: '#fff',
                    minWidth: 150,
                    borderRight: '1px solid #ccc',
                    fontWeight: 'bold',
                  }}>
                  {ALL_ROLES[role]}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    borderRight: '1px solid #ccc',
                    cursor: costRate ? 'pointer' : 'default',
                  }}
                  onClick={() => costRate && onRoleClick?.(role, 'cost')}>
                  {costRate
                    ? `$${Number(costRate.workingAwayFromHomeRate).toFixed(2)}`
                    : '-'}
                </TableCell>
                {allBillingBranches.length > 0 ? (
                  allBillingBranches.map((b) => {
                    const wafhBillingRate =
                      getLatestWafhBillingRateForRoleAndBranch(role, b);
                    return (
                      <TableCell
                        key={b}
                        align="center"
                        sx={{
                          borderRight: '1px solid #ccc',
                          cursor:
                            wafhBillingRate !== null ? 'pointer' : 'default',
                        }}
                        onClick={() =>
                          wafhBillingRate !== null &&
                          onRoleClick?.(role, 'billing')
                        }>
                        {wafhBillingRate !== null
                          ? `$${wafhBillingRate.toFixed(2)}`
                          : '-'}
                      </TableCell>
                    );
                  })
                ) : (
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ccc' }}>
                    -
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
